import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  LocalOffer as CouponIcon,
} from '@mui/icons-material';
import { collection, query, where, orderBy, onSnapshot, doc, addDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expiresAt: Date | null;
  maxUsage: number;
  usageCount: number;
  minOrderAmount: number;
  targetType: 'all' | 'category' | 'product';
  targetIds: string[];
  memo: string;
  isActive: boolean;
  lastUsedAt: Date | null;
  totalDiscountAmount: number;
  createdAt: Date;
  updatedAt: Date;
}

const generateCouponCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const getEmptyFormData = () => ({
  code: generateCouponCode(),
  discountType: 'percentage' as 'percentage' | 'fixed',
  discountValue: 0,
  expiresAt: '',
  maxUsage: 0,
  minOrderAmount: 0,
  targetType: 'all' as 'all' | 'category' | 'product',
  targetIds: [] as string[],
  memo: '',
  isActive: true,
});

type FilterTab = 'all' | 'active' | 'expired' | 'used';

const Coupons: React.FC = () => {
  const { shop } = useAuth();
  const { t } = useLanguage();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState(getEmptyFormData());

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<Coupon | null>(null);

  // Filter & search
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Load coupons
  useEffect(() => {
    if (!shop) return;

    const q = query(
      collection(db, `shops/${shop.id}/coupons`),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        expiresAt: docSnap.data().expiresAt?.toDate() || null,
        lastUsedAt: docSnap.data().lastUsedAt?.toDate() || null,
        createdAt: docSnap.data().createdAt?.toDate() || new Date(),
        updatedAt: docSnap.data().updatedAt?.toDate() || new Date(),
      })) as Coupon[];
      setCoupons(data);
      setLoading(false);
    }, (err) => {
      console.error('Error loading coupons:', err);
      setError(t('couponLoadError'));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [shop]);

  // Filter coupons
  const filteredCoupons = useMemo(() => {
    const now = new Date();
    let filtered = coupons;

    // Tab filter
    switch (filterTab) {
      case 'active':
        filtered = filtered.filter(c =>
          c.isActive && (!c.expiresAt || c.expiresAt > now) &&
          (c.maxUsage === 0 || c.usageCount < c.maxUsage)
        );
        break;
      case 'expired':
        filtered = filtered.filter(c =>
          c.expiresAt && c.expiresAt <= now
        );
        break;
      case 'used':
        filtered = filtered.filter(c =>
          c.maxUsage > 0 && c.usageCount >= c.maxUsage
        );
        break;
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(c =>
        c.code.toLowerCase().includes(q) ||
        c.memo.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [coupons, filterTab, searchQuery]);

  const getCouponStatus = (coupon: Coupon): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
    const now = new Date();

    if (!coupon.isActive) {
      return { label: t('couponInactive'), color: 'default' };
    }
    if (coupon.expiresAt && coupon.expiresAt <= now) {
      return { label: t('couponExpired'), color: 'error' };
    }
    if (coupon.maxUsage > 0 && coupon.usageCount >= coupon.maxUsage) {
      return { label: t('couponUsedUp'), color: 'warning' };
    }
    return { label: t('couponActive'), color: 'success' };
  };

  const handleOpenCreate = () => {
    setEditingCoupon(null);
    setFormData(getEmptyFormData());
    setOpenDialog(true);
  };

  const handleOpenEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      expiresAt: coupon.expiresAt
        ? coupon.expiresAt.toISOString().slice(0, 16)
        : '',
      maxUsage: coupon.maxUsage,
      minOrderAmount: coupon.minOrderAmount,
      targetType: coupon.targetType,
      targetIds: coupon.targetIds,
      memo: coupon.memo,
      isActive: coupon.isActive,
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!shop) return;

    if (!formData.code.trim()) {
      setError(t('couponCodeRequired'));
      return;
    }
    if (formData.discountValue <= 0) {
      setError(t('couponValueRequired'));
      return;
    }
    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      setError(t('couponPercentageMax'));
      return;
    }
    // 最低注文金額だけ下限の検証が無く、負の値がそのまま保存されていた。
    // 負だと「いくらでも使えるクーポン」になる。
    if (!Number.isFinite(Number(formData.minOrderAmount)) || Number(formData.minOrderAmount) < 0) {
      setError('最低注文金額は0以上にしてください');
      return;
    }

    // Check for duplicate code (excluding current coupon if editing)
    const duplicate = coupons.find(c =>
      c.code.toUpperCase() === formData.code.trim().toUpperCase() &&
      c.id !== editingCoupon?.id
    );
    if (duplicate) {
      setError(t('couponCodeDuplicate'));
      return;
    }

    try {
      const couponData: any = {
        code: formData.code.trim().toUpperCase(),
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        expiresAt: formData.expiresAt ? Timestamp.fromDate(new Date(formData.expiresAt)) : null,
        maxUsage: formData.maxUsage,
        minOrderAmount: formData.minOrderAmount,
        targetType: formData.targetType,
        targetIds: formData.targetIds,
        memo: formData.memo,
        isActive: formData.isActive,
        updatedAt: Timestamp.now(),
      };

      if (editingCoupon) {
        await updateDoc(doc(db, `shops/${shop.id}/coupons`, editingCoupon.id), couponData);
        setSuccess(t('couponUpdated'));
      } else {
        couponData.usageCount = 0;
        couponData.totalDiscountAmount = 0;
        couponData.lastUsedAt = null;
        couponData.createdAt = Timestamp.now();
        await addDoc(collection(db, `shops/${shop.id}/coupons`), couponData);
        setSuccess(t('couponCreated'));
      }

      setOpenDialog(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving coupon:', err);
      setError(t('saveFailed'));
    }
  };

  const handleDelete = async () => {
    if (!shop || !deleteTarget) return;

    try {
      await deleteDoc(doc(db, `shops/${shop.id}/coupons`, deleteTarget.id));
      setDeleteTarget(null);
      setSuccess(t('couponDeleted'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting coupon:', err);
      setError(t('deleteFailed'));
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccess(t('couponCodeCopied'));
    setTimeout(() => setSuccess(null), 2000);
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '-';
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDiscount = (coupon: Coupon): string => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%`;
    }
    return `\u00a5${coupon.discountValue.toLocaleString()}`;
  };

  // Summary stats
  const stats = useMemo(() => {
    const now = new Date();
    const activeCoupons = coupons.filter(c =>
      c.isActive && (!c.expiresAt || c.expiresAt > now) &&
      (c.maxUsage === 0 || c.usageCount < c.maxUsage)
    );
    const totalUsage = coupons.reduce((sum, c) => sum + c.usageCount, 0);
    const totalDiscountAmount = coupons.reduce((sum, c) => sum + c.totalDiscountAmount, 0);
    return {
      total: coupons.length,
      active: activeCoupons.length,
      totalUsage,
      totalDiscountAmount,
    };
  }, [coupons]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('couponManagement')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          {t('couponCreate')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" color="primary">{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary">{t('couponTotal')}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" color="success.main">{stats.active}</Typography>
            <Typography variant="body2" color="text.secondary">{t('couponActiveCount')}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5">{stats.totalUsage}</Typography>
            <Typography variant="body2" color="text.secondary">{t('couponTotalUsage')}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" color="error.main">
              {`\u00a5${stats.totalDiscountAmount.toLocaleString()}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t('couponTotalDiscount')}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter tabs & search */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, px: 2, pt: 1 }}>
          <Tabs
            value={filterTab}
            onChange={(_, v) => setFilterTab(v)}
            sx={{ flex: 1 }}
          >
            <Tab value="all" label={t('all')} />
            <Tab value="active" label={t('couponActive')} />
            <Tab value="expired" label={t('couponExpired')} />
            <Tab value="used" label={t('couponUsedUp')} />
          </Tabs>
          <TextField
            size="small"
            placeholder={t('couponSearchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ width: 250 }}
          />
        </Box>
      </Paper>

      {/* Coupon table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('couponCode')}</TableCell>
              <TableCell>{t('couponDiscount')}</TableCell>
              <TableCell>{t('couponExpiry')}</TableCell>
              <TableCell>{t('couponUsage')}</TableCell>
              <TableCell>{t('couponMinOrder')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('couponMemo')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCoupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CouponIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">{t('noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCoupons.map((coupon) => {
                const statusInfo = getCouponStatus(coupon);
                return (
                  <TableRow key={coupon.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                          {coupon.code}
                        </Typography>
                        <Tooltip title={t('couponCopyCode')}>
                          <IconButton size="small" onClick={() => handleCopyCode(coupon.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDiscount(coupon)}
                        color={coupon.discountType === 'percentage' ? 'primary' : 'secondary'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.expiresAt ? formatDate(coupon.expiresAt) : t('couponNoExpiry')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.usageCount}
                        {coupon.maxUsage > 0 ? ` / ${coupon.maxUsage}` : ` (${t('couponUnlimited')})`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {coupon.minOrderAmount > 0
                        ? `\u00a5${coupon.minOrderAmount.toLocaleString()}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {coupon.memo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('edit')}>
                        <IconButton size="small" onClick={() => handleOpenEdit(coupon)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(coupon)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCoupon ? t('couponEdit') : t('couponCreate')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Code */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('couponCode')}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                fullWidth
                inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 2 } }}
              />
              {!editingCoupon && (
                <Button
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, code: generateCouponCode() })}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('couponAutoGenerate')}
                </Button>
              )}
            </Box>

            {/* Discount type & value */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('couponDiscountType')}</InputLabel>
                  <Select
                    value={formData.discountType}
                    label={t('couponDiscountType')}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as any })}
                  >
                    <MenuItem value="percentage">{t('couponPercentage')}</MenuItem>
                    <MenuItem value="fixed">{t('couponFixed')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={formData.discountType === 'percentage' ? t('couponPercentageValue') : t('couponFixedValue')}
                  type="number"
                  value={formData.discountValue || ''}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  fullWidth
                  inputProps={{
                    min: 0,
                    max: formData.discountType === 'percentage' ? 100 : undefined,
                  }}
                />
              </Grid>
            </Grid>

            {/* Expiry */}
            <TextField
              label={t('couponExpiry')}
              type="datetime-local"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={t('couponExpiryHelp')}
            />

            {/* Max usage */}
            <TextField
              label={t('couponMaxUsage')}
              type="number"
              value={formData.maxUsage || ''}
              onChange={(e) => setFormData({ ...formData, maxUsage: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
              helperText={t('couponMaxUsageHelp')}
            />

            {/* Min order amount */}
            <TextField
              label={t('couponMinOrder')}
              type="number"
              value={formData.minOrderAmount || ''}
              onChange={(e) => setFormData({ ...formData, minOrderAmount: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
            />

            {/* Target type */}
            <FormControl fullWidth>
              <InputLabel>{t('couponTarget')}</InputLabel>
              <Select
                value={formData.targetType}
                label={t('couponTarget')}
                onChange={(e) => setFormData({ ...formData, targetType: e.target.value as any })}
              >
                <MenuItem value="all">{t('couponTargetAll')}</MenuItem>
                <MenuItem value="category">{t('couponTargetCategory')}</MenuItem>
                <MenuItem value="product">{t('couponTargetProduct')}</MenuItem>
              </Select>
            </FormControl>

            {/* Memo */}
            <TextField
              label={t('couponMemo')}
              value={formData.memo}
              onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingCoupon ? t('update') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('couponDeleteConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('couponDeleteMessage')}: <strong>{deleteTarget?.code}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Coupons;
